import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import { logButtonClickAtom } from "@sunrise/analytics";
import { actionLocationNavigate, locationAtom } from "@sunrise/location";
import { playerCurrentEpgItemAtom } from "@sunrise/player";
import { nowAtom } from "@sunrise/time";
import { isDefined, isNil } from "@sunrise/utils";
import { programIsPlayingAtTime } from "@sunrise/yallo-epg";
import { type GuideChannel } from "@sunrise/yallo-guide";
import { useVirtualizer } from "@tanstack/react-virtual";
import { isWithinInterval } from "date-fns";
import { addDays, addMinutes, set, subMinutes } from "date-fns";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { route } from "@/config/route";
import { SCREEN_HEIGHT_IN_PX } from "@/core/constants";
import { GuideChannels } from "@/features/guide/guide-channels";
import { GuideNowBar } from "@/features/guide/guide-now-bar";
import { TimeBlocksBar } from "@/features/guide/time-blocks-bar";
import { useGridAutoSelectAndScrollAndNavigate } from "@/modules/guide/hooks/use-grid-auto-select-and-scroll-and-navigate";
import { UseGuideDataOnVisibleDataChangedCallback } from "@/modules/guide/hooks/use-guide-data";
import {
  actionGuideSetGridFocused,
  gridStateAtom,
  selectorGuideSelection,
  selectorIsGridFocused,
} from "@/modules/guide/store";
import { getProgramsStartingAtTime } from "@/modules/guide/utils/get-programs-starting-at-time";
import { pixelsToTime } from "@/modules/guide/utils/pixels-to-time";
import { timeToPixels } from "@/modules/guide/utils/time-to-pixels";
import { useKeyboardNavigation } from "@/modules/keyboard-navigation";
import { mergeRefs } from "@/utils/merge-refs";
import { isArrowLeftKey, isArrowUpKey } from "@/utils/navigation";

import { OnBlurRequestFunction } from "../settings/types";
import {
  GUIDE_GRID_CHANNELS_COLUMN_WIDTH_IN_PX,
  GUIDE_GRID_CHANNEL_HEIGHT_IN_PX,
  GUIDE_GRID_TIME_INDICATOR_BAR_HEIGHT_IN_PX,
  GUIDE_PROGRAM_PREVIEW_HEIGHT_IN_PX,
  GUIDE_THROTTLE_ON_LONG_PRESS_IN_MS,
  NOW_BAR_WIDTH_IN_PX,
} from "./constants";
import * as styles from "./guide-grid.css";
import { GuideProgramItem } from "./guide-program-item";
import type { ChannelId } from "@sunrise/backend-types-core";

const CHANNEL_COUNT_VISIBLE =
  (SCREEN_HEIGHT_IN_PX -
    GUIDE_PROGRAM_PREVIEW_HEIGHT_IN_PX -
    GUIDE_GRID_TIME_INDICATOR_BAR_HEIGHT_IN_PX) /
  GUIDE_GRID_CHANNEL_HEIGHT_IN_PX;

const TIME_BLOCKS_VISIBLE = 5;
const TIME_BLOCK_SIZE_IN_MINUTES = 30;
const CHANNEL_TIME_FRAME_SIZE_IN_MINUTES = 24 * 60;
const TIME_FRAME_BUFFER = 0.5;
// This means, we want 12 hour of buffer.
// So if the first block in the grid starts at 10:00 I am 2 hours under the buffer for the start.
// That means we need to load in the 24 hours from the day before as well.
const TIME_FRAME_BUFFER_IN_MINUTES =
  CHANNEL_TIME_FRAME_SIZE_IN_MINUTES * TIME_FRAME_BUFFER;

export type GuideGridProps = {
  data: GuideChannel[];
  disableAnimations?: boolean;
  endTime: Date;
  focusKey?: string;
  height?: number;
  offset?: { x: number; y: number };
  onBlurRequest?: OnBlurRequestFunction;
  onVisibleDataChanged?: UseGuideDataOnVisibleDataChangedCallback;
  startTime: Date;
  testId?: string;
  width?: number;
};

export type TimeBlock = {
  id: number;
  date: Date;
};

/**
 * This is purely the grid section of the TV-Guide feature.
 * It should always be kept as a pure component and have nothing to do with state management.
 * All data should be passed to it. All state change requests should be emitted as callbacks.
 *
 * Everything can be wired to the state manager at a higher level.
 *
 * NOTE: The width & height needs to be consistent. As soon as the width / height of the element changes, the virtualizers will get confused.
 */
export function GuideGrid({
  data,
  disableAnimations = false,
  endTime,
  focusKey,
  height,
  offset,
  onBlurRequest,
  onVisibleDataChanged,
  startTime,
  testId = "guide-grid",
  width,
}: GuideGridProps): ReactNode {
  const gridRef = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const dispatchGridAction = useSetAtom(gridStateAtom);
  const selection = useAtomValue(selectorGuideSelection);
  const playingEpgItem = useAtomValue(playerCurrentEpgItemAtom);

  const isProgramFocused = useAtomValue(selectorIsGridFocused);

  const gridHeightInPx = height ?? gridRef.current?.offsetHeight ?? 0;
  const gridWidthInPx = width ?? gridRef.current?.offsetWidth ?? 0;

  const programsAvailableWidth =
    gridWidthInPx - GUIDE_GRID_CHANNELS_COLUMN_WIDTH_IN_PX;
  const timeBlockWidthInPx = Math.floor(
    programsAvailableWidth / TIME_BLOCKS_VISIBLE,
  );

  const channelHeightInPx =
    (gridHeightInPx - GUIDE_GRID_TIME_INDICATOR_BAR_HEIGHT_IN_PX) /
    CHANNEL_COUNT_VISIBLE;

  // This needs to be consistent .... if it is not we somehow don't render the correct width for the programs anymore.
  // NOTE: I attempted to add different count on the virtualizers as long as the width was not stable.
  //       This sometimes worked. But not always. It's best to pass in a fixed height / width into the component.
  const oneMinuteWidthInPx = timeBlockWidthInPx / TIME_BLOCK_SIZE_IN_MINUTES;

  /**
   * Generates an array of timeblocks based on the start and end time and the TIMEBLOCK_SIZE_IN_MINUTES.
   */
  const timeBlocks = useMemo<TimeBlock[]>(() => {
    const rangeInMs = endTime.getTime() - startTime.getTime();
    const rangeInMinutes = rangeInMs / 1000 / 60;
    const rangeInTimeBlocks = rangeInMinutes / TIME_BLOCK_SIZE_IN_MINUTES;
    const count = Math.floor(rangeInTimeBlocks);

    return new Array(count).fill(null).map((_, index) => {
      return {
        id: index,
        date: addMinutes(startTime, index * TIME_BLOCK_SIZE_IN_MINUTES),
      };
    });
  }, [startTime, endTime]);

  const timeBlockVirtualizer = useVirtualizer({
    count: timeBlocks.length,
    estimateSize: () => timeBlockWidthInPx,
    getScrollElement: () => gridRef.current,
    horizontal: true,
  });

  const channelsVirtualizer = useVirtualizer({
    count: data.length,
    estimateSize: () => channelHeightInPx,
    getScrollElement: () => gridRef.current,
  });

  const virtualChannels = channelsVirtualizer.getVirtualItems();
  const virtualTimeBlocks = timeBlockVirtualizer.getVirtualItems();

  // Whenever the size of the container changes, we need to measure the virtualizers again.
  // Else we would keep the calculations and the grid would look really funny.
  useEffect(() => {
    if (gridHeightInPx > 0) {
      channelsVirtualizer.measure();
    }
  }, [channelsVirtualizer, gridHeightInPx]);
  useEffect(() => {
    if (gridWidthInPx > 0) {
      timeBlockVirtualizer.measure();
    }
  }, [timeBlockVirtualizer, gridWidthInPx]);

  useEffect(() => {
    if (!offset) {
      return;
    }
    // NOTE: Absolutely no idea why this needs to be delayed.
    //       Checked to also do it after the guideRef and timeBlocksRef exist. No difference.
    //       Tried useLayoutEffect. No difference.
    //       On slow devices, it might take some more tries until the grid element is fully populated
    let timeout: ReturnType<typeof setTimeout>;
    const scrollToCurrentPosition = (count = 0): void => {
      timeout = setTimeout(() => {
        if (count > 5 || !gridRef.current) {
          return;
        }

        if (offset.x > 0 || offset.y > 0) {
          gridRef.current.scroll({
            left: offset.x,
            top: offset.y,
            behavior: "auto",
          });
          // Only mark started after we have scrolled to an offset.
          setStarted(true);
        }
      }, 10);
    };

    scrollToCurrentPosition();

    return () => clearTimeout(timeout);
  }, [offset]);

  const channelBarCutoff = offset ? offset.x : undefined;

  // Always disable animations on initial render.
  const shouldDisableAnimations = started ? disableAnimations : true;

  const now = useAtomValue(nowAtom);

  const fullWidth = timeBlockVirtualizer.getTotalSize();

  const { focused, ref: gridFocusRef } = useFocusable({
    focusKey,
    onArrowPress: function handleArrowPress() {
      // Important we return false here.
      // We don't want to suddenly lose focus from the grid in case a navigation didn't work as expected.
      return false;
    },
    onBlur() {
      navigation.flush();
      dispatchGridAction(actionGuideSetGridFocused(false));
    },
    onFocus() {
      dispatchGridAction(actionGuideSetGridFocused(true));
    },
    // It's a boundary because we will be managing the focus manually inside the grid.
    isFocusBoundary: true,
  });

  const dispatchLocation = useSetAtom(locationAtom);
  useKeyboardNavigation({
    onBack: () => onBlurRequest?.("back"),
    onEnter: useAtomCallback(
      useCallback(
        async (get) => {
          const selectionInternal = get(selectorGuideSelection);
          if (!selectionInternal) {
            return;
          }

          const log = get(logButtonClickAtom);

          await log.invoke({
            type: "to_epg_item",
            epgId: selectionInternal.epgId,
          });

          dispatchLocation(
            actionLocationNavigate(
              route.details.root({
                epgId: selectionInternal.epgId,
                assetId: selectionInternal.assetId,
              }),
            ),
          );
        },
        [dispatchLocation],
      ),
    ),
    onArrow: (direction) => {
      // This is dangerous, I know. But the onArrowPress directions are only up/right/down/left. Which are the NavigationDirections.
      // The idea here is that if we were able to navigate in the grid, we stop all other navigation. Else, we let the normal navigation continue.
      // That means if we can no longer go up because we are at the top channel, we would direct to the onBlurRequest('up').
      if (navigation.navigate(direction as NavigationDirection)) {
        return;
      }

      if (isArrowUpKey(direction) && onBlurRequest) {
        onBlurRequest("up");
        return;
      }

      if (isArrowLeftKey(direction) && onBlurRequest) {
        onBlurRequest("left");
      }
    },
    isEnabled: focused,
    repeatThrottle: GUIDE_THROTTLE_ON_LONG_PRESS_IN_MS,
  });

  const channelToPixels = useCallback(
    (channelId: ChannelId) => {
      const idx = data.findIndex((c) => c.id === channelId);
      if (idx === -1) {
        return 0;
      }

      // NOTE: I tried using the virtualizer to get the virtual item and then the start of it.
      //       But when scrolling fast this would just not be accurate. The virtual item would not be known yet and so it would return 0.
      return channelHeightInPx * idx;
    },
    [channelHeightInPx, data],
  );

  const timeToPixelsFn = useCallback(
    (time: Date) => {
      return timeToPixels(time, startTime, endTime, fullWidth);
    },
    [startTime, endTime, fullWidth],
  );

  const pixelsToTimeFn = useCallback(
    (pixels: number) => {
      return pixelsToTime(pixels, startTime, endTime, fullWidth);
    },
    [startTime, endTime, fullWidth],
  );

  const navigation = useGridAutoSelectAndScrollAndNavigate({
    channelHeightInPx,
    data,
    height: gridHeightInPx,
    width: gridWidthInPx - GUIDE_GRID_CHANNELS_COLUMN_WIDTH_IN_PX,
    channelToPixels,
    timeToPixels: timeToPixelsFn,
    pixelsToTime: pixelsToTimeFn,
    isWithinGuideRange: useCallback(
      (time) => isWithinInterval(time, { start: startTime, end: endTime }),
      [startTime, endTime],
    ),
  });

  const nowOffset = useMemo(() => {
    // Not sure if we want to clip time like this. But it will make it more aligned.
    const time = set(now, {
      seconds: 0,
      milliseconds: 0,
    });
    const pixels = timeToPixels(time, startTime, endTime, fullWidth);

    return (
      // TODO: Is there a way to fix the nowBar styling so that we can place it in the center of its marginLeft?
      pixels - NOW_BAR_WIDTH_IN_PX / 2
    );
  }, [now, startTime, endTime, fullWidth]);

  const timeRangeVisibleInMinutes =
    TIME_BLOCKS_VISIBLE * TIME_BLOCK_SIZE_IN_MINUTES;

  const firstTimeVisible = timeBlocks[virtualTimeBlocks[0]?.index ?? -1]?.date;
  const firstChannelIndexVisible = virtualChannels[0]?.index;

  /**
   * We need to communicate what data is currently visible to the hook.
   * So it can load in the correct data.
   *
   * This effect depends mostly on firstChannelIndexVisible and firstTimeVisible
   */
  useEffect(() => {
    if (!onVisibleDataChanged || !firstTimeVisible) {
      return;
    }

    const lastVirtualTimeBlock =
      virtualTimeBlocks[virtualTimeBlocks.length - 1];
    const lastBlock = isDefined(lastVirtualTimeBlock)
      ? timeBlocks[lastVirtualTimeBlock.index] ?? null
      : null;

    const initialStartTime = firstTimeVisible;
    const initialEndTime = lastBlock
      ? lastBlock.date
      : addDays(initialStartTime, 1);

    // We want to make sure to load in the previous day if the startTime is too close to the day before.

    const channelIndex = Math.max(
      (firstChannelIndexVisible ?? 0) - CHANNEL_COUNT_VISIBLE,
      0,
    );

    const lastChannelIndexVisible =
      virtualChannels[virtualChannels.length - 1]?.index;

    const endChannelIndex =
      Math.max(lastChannelIndexVisible ?? 0, channelIndex) +
      CHANNEL_COUNT_VISIBLE;

    onVisibleDataChanged(
      data.slice(channelIndex, endChannelIndex).map((v) => v.id),
      subMinutes(initialStartTime, TIME_FRAME_BUFFER_IN_MINUTES),
      addMinutes(initialEndTime, TIME_FRAME_BUFFER_IN_MINUTES),
    );
  }, [
    onVisibleDataChanged,
    timeRangeVisibleInMinutes,
    data,
    firstTimeVisible,
    firstChannelIndexVisible,
    virtualChannels,
    virtualTimeBlocks,
    timeBlocks,
  ]);

  if (data.length === 0) {
    return null;
  }

  // We wire up the virtualizers to the x/y axis of this container.
  // This container's offset will be constantly changed when navigating.
  return (
    <div
      ref={mergeRefs(gridRef, gridFocusRef)}
      className={styles.scrollContainer}
      data-testid={testId}
      data-focused={focused}
      style={{
        scrollBehavior: shouldDisableAnimations ? "unset" : "smooth",
      }}
    >
      <TimeBlocksBar
        style={{
          width: fullWidth,
        }}
        nowOffset={nowOffset}
        virtualTimeBlocks={virtualTimeBlocks}
        timeBlocks={timeBlocks}
      />

      {/* under the hours bar we render a div that covers the full width and height of the virtualizers.
      So we can already scroll to any position we like. It also contains the sticky channelbar.*/}
      <div
        className={styles.scrollContainerInner}
        style={{
          height: channelsVirtualizer.getTotalSize(),
          width: fullWidth,
        }}
      >
        <Suspense>
          <GuideChannels
            size={channelsVirtualizer.getTotalSize()}
            items={virtualChannels}
            channels={data}
            channelHeightInPx={channelHeightInPx}
          />
        </Suspense>

        <GuideNowBar
          nowOffset={nowOffset}
          size={channelsVirtualizer.getTotalSize()}
        />

        {/* this is a virtualized row per channel */}
        {virtualChannels.map((virtualItem) => {
          const channel = data[virtualItem.index];

          if (isNil(channel)) {
            return null;
          }

          const isChannelSelected =
            !!selection && channel.id === selection.channelId;

          return (
            <div
              key={virtualItem.key}
              className={styles.channelRow}
              data-testid={`${testId}-channel-${channel.id}`}
              style={{
                height: channelHeightInPx,
                // We need to make sure the width of the channelbar is also the width of the hours.
                width: fullWidth,
                transform: `translateY(${virtualItem.start}px)`,
                willChange: "transform",
              }}
            >
              {/* in every channel row we again loop over the hours virtualizer to
              render the programs associated with that hour for the current channel.
              But they will have an offset releted to the hour. */}
              {virtualTimeBlocks.map((virtualTimeBlockForChannel, i) => {
                const timeBlock = timeBlocks[virtualTimeBlockForChannel.index];

                if (isNil(timeBlock)) {
                  return null;
                }

                const programs = getProgramsStartingAtTime(
                  timeBlock.date,
                  TIME_BLOCK_SIZE_IN_MINUTES,
                  channel.items,
                  i === 0,
                );

                const offsetForHour = virtualTimeBlockForChannel.start;

                return programs.map((program) => {
                  const isLive = programIsPlayingAtTime(program, now);
                  const isFocused =
                    isProgramFocused &&
                    isChannelSelected &&
                    program.id === selection.epgId;

                  return (
                    <GuideProgramItem
                      key={program.id}
                      isLive={isLive}
                      // NOTE: In the context of GuideProgramItem, selected means isPlaying.
                      //       Not that it is selected by the user in the grid.
                      isActive={playingEpgItem?.id === program.id}
                      isFocused={isFocused}
                      program={program}
                      oneMinuteWidthInPx={oneMinuteWidthInPx}
                      channelHeightInPx={channelHeightInPx}
                      channelBarCutoffInPx={channelBarCutoff}
                      disableAnimations={shouldDisableAnimations}
                      offset={offsetForHour}
                      timeOffset={timeBlock.date}
                      fullGuideWidth={fullWidth}
                    />
                  );
                });
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
